import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AkButton from '@atlaskit/button/custom-theme-button';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import { token } from '@atlaskit/tokens';

import { WebItemAnalyticsWrapper } from '@confluence/analytics/entry-points/WebItemAnalyticsWrapper.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { ATTACHMENTS_PAGE } from '@confluence/named-routes/entry-points/routes.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewAttachmentsButtonContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		cursor: 'pointer',
		height: '30px',
		width: '30px',
		padding: 0,
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a, a:hover': {
		color: token('color.text.subtle'),
	},
});

const i18n = defineMessages({
	attachments: {
		id: 'attachments.view-attachments-button',
		defaultMessage: 'View attachments',
		description:
			'Explaining that click on a button will take user to a list of attachments for the page',
	},
});

const ATTACHMENTS_ANALYTICS_FROM_VIEW = {
	type: 'sendUIEvent',
	data: {
		action: 'clicked',
		actionSubject: 'ViewAttachmentsButton',
		source: 'attachments',
		attributes: {
			triggeredFrom: 'fromView',
		},
	},
};

export const ATTACHMENTS_ANALYTICS_FROM_ACTIONS_MENU = {
	type: 'sendUIEvent',
	data: {
		action: 'clicked',
		actionSubject: 'ViewAttachmentsButton',
		source: 'attachments',
		attributes: {
			triggeredFrom: 'actionsMenu',
		},
	},
};

export const ViewAttachmentsButtonComponent = ({ intl }) => {
	const label = intl.formatMessage(i18n.attachments);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			actionSubject: 'feature',
			action: 'exposed',
			source: 'ViewAttachmentsButton',
			attributes: {
				isPageSSRd: window.__SSR_RENDERED__,
			},
		},
	}).fire();

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<ViewAttachmentsButtonContainer>
				<AkButton appearance="subtle">
					<WebItemAnalyticsWrapper analyticsData={ATTACHMENTS_ANALYTICS_FROM_VIEW}>
						{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
						<a
							href={
								expValEquals('cc_attachments_page', 'isEnabled', true)
									? ATTACHMENTS_PAGE.toUrl({ spaceKey, contentId })
									: `${CONTEXT_PATH}/pages/viewpageattachments.action?pageId=${contentId}&metadataLink=true`
							}
						>
							<AttachmentIcon spacing="spacious" label={label} />
						</a>
					</WebItemAnalyticsWrapper>
				</AkButton>
			</ViewAttachmentsButtonContainer>
		</ErrorBoundary>
	);
};

ViewAttachmentsButtonComponent.propTypes = {
	intl: PropTypes.object,
};

export const ViewAttachmentsButton = injectIntl(ViewAttachmentsButtonComponent);
